import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Users, CalendarCheck, ChevronRight, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  bozza: "bg-slate-100 text-slate-700",
  programmato: "bg-sky-50 text-sky-700",
  "in svolgimento": "bg-emerald-50 text-emerald-700",
  concluso: "bg-slate-200 text-slate-700",
  archiviato: "bg-slate-100 text-slate-500",
};

const byStart = (a, b) => (a.first_lesson_date || "9999").localeCompare(b.first_lesson_date || "9999");
const orderCourses = (list) => {
  const prog = list.filter((c) => c.status === "programmato").sort(byStart);
  let i = 0;
  return list.map((c) => (c.status === "programmato" ? prog[i++] : c));
};

const FILTERS = [["all", "Tutti"], ["in svolgimento", "In svolgimento"], ["programmato", "Programmati"], ["concluso", "Conclusi"], ["archiviato", "Archiviati"]];

const empty = {
  code: "", title: "", edition: "", description: "",
  max_absence_pct: 20, presence_threshold_pct: 80,
  platform: "BigBlueButton", status: "programmato", manager: "", tutor: "", tutor_email: "", project_manager: "", project_manager_email: "",
};

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/courses").then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (e, c) => { e.stopPropagation(); setEditId(c.id); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async () => {
    const body = { ...form, max_absence_pct: Number(form.max_absence_pct), presence_threshold_pct: Number(form.presence_threshold_pct) };
    try {
      if (editId) await api.put(`/courses/${editId}`, body);
      else await api.post("/courses", body);
      toast.success(editId ? "Corso aggiornato" : "Corso creato");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const del = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente il corso "${c.title}"? Verranno rimossi lezioni, iscrizioni e presenze associate.`)) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success("Corso eliminato");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Corsi" subtitle="Gestione dei corsi in videoconferenza.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-course-btn" onClick={openNew} className="bg-[#0B192C] hover:bg-[#07111E]">
              <Plus size={16} className="mr-1" /> Nuovo corso
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="course-dialog">
            <DialogHeader><DialogTitle>{editId ? "Modifica corso" : "Nuovo corso"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Codice corso</Label><Input data-testid="course-code" value={form.code} onChange={set("code")} /></div>
              <div><Label>Edizione</Label><Input value={form.edition} onChange={set("edition")} /></div>
              <div className="col-span-2"><Label>Titolo</Label><Input data-testid="course-title" value={form.title} onChange={set("title")} /></div>
              <div className="col-span-2"><Label>Descrizione</Label><Textarea value={form.description} onChange={set("description")} /></div>
              <div><Label>% max assenza</Label><Input type="number" value={form.max_absence_pct} onChange={set("max_absence_pct")} /></div>
              <div><Label>Soglia presenza % (presente)</Label><Input type="number" value={form.presence_threshold_pct} onChange={set("presence_threshold_pct")} /></div>
              <div><Label>Piattaforma</Label><Input value={form.platform} onChange={set("platform")} /></div>
              <div><Label>Responsabile (Direttore del corso)</Label><Input value={form.manager || ""} onChange={set("manager")} data-testid="course-manager" /></div>
              <div><Label>Tutor</Label><Input value={form.tutor || ""} onChange={set("tutor")} data-testid="course-tutor" /></div>
              <div><Label>E-mail Tutor</Label><Input type="email" value={form.tutor_email || ""} onChange={set("tutor_email")} data-testid="course-tutor-email" /></div>
              <div><Label>Responsabile del Progetto Formativo</Label><Input value={form.project_manager || ""} onChange={set("project_manager")} data-testid="course-pm" /></div>
              <div><Label>E-mail Responsabile Progetto Formativo</Label><Input type="email" value={form.project_manager_email || ""} onChange={set("project_manager_email")} data-testid="course-pm-email" /></div>
              <div><Label>Stato</Label>
                <select data-testid="course-status" value={form.status} onChange={set("status")} className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm capitalize">
                  {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Si aggiorna da solo in base alle date delle lezioni; "archiviato" resta manuale.</p>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-course-btn" onClick={save} className="bg-[#008C45] hover:bg-[#006B34]">{editId ? "Salva modifiche" : "Crea corso"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="course-filter">
        {FILTERS.map(([k, l]) => {
          const n = k === "all" ? courses.length : courses.filter((c) => c.status === k).length;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`course-filter-${k.replace(" ", "-")}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === k ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {l} <span className={`ml-1 tabular ${filter === k ? "text-white/70" : "text-slate-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orderCourses(courses.filter((c) => filter === "all" || c.status === filter)).map((c) => (
          <Card key={c.id} data-testid={`course-card-${c.code}`}
            onClick={() => navigate(`/corsi/${c.id}`)}
            className="p-5 border-slate-200 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all group">
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span>
              <span className="font-mono text-xs text-slate-400">{c.code}</span>
            </div>
            <h3 className="font-display font-bold text-slate-900 leading-tight group-hover:text-[#008C45] transition-colors">{c.title}</h3>
            <p className="text-xs text-slate-500 mt-1">{c.edition}{c.first_lesson_date && <span data-testid={`course-start-${c.code}`}> · Inizio {new Date(`${c.first_lesson_date}T00:00:00`).toLocaleDateString("it-IT")}</span>}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Users size={15} /> {c.num_participants}</span>
              <span className="flex items-center gap-1.5"><CalendarCheck size={15} /> {c.num_lessons_done}/{c.num_lessons}</span>
              <span className="ml-auto flex items-center gap-1">
                <button data-testid={`edit-course-${c.code}`} onClick={(e) => openEdit(e, c)}
                  title="Modifica corso"
                  className="p-1.5 rounded-md text-slate-400 hover:text-sky-700 hover:bg-sky-50 transition-colors">
                  <Pencil size={16} />
                </button>
                <button data-testid={`delete-course-${c.code}`} onClick={(e) => del(e, c)}
                  title="Elimina corso"
                  className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                  <Trash2 size={16} />
                </button>
                <ChevronRight size={18} className="text-slate-400 group-hover:text-[#008C45] transition-colors" />
              </span>
            </div>
          </Card>
        ))}
        {courses.length > 0 && !courses.some((c) => filter === "all" || c.status === filter) && (
          <p className="text-slate-500 text-sm col-span-full" data-testid="course-filter-empty">Nessun corso in questo stato.</p>
        )}
        {courses.length === 0 && <p className="text-slate-500 col-span-full">Nessun corso. Crea il primo corso.</p>}
      </div>
    </div>
  );
}
